import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BokkzVpbDataService,
  CorporateTaxEntity,
  VpbAangifte,
  VpbBerekening
} from '@snelstart-ventures/fiscaal/domain-logic';
import {AbstractControl, FormArray, FormControl, FormGroup} from '@angular/forms';
import {debounceTime, filter, Observable, shareReplay, tap} from 'rxjs';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {SharedUiTooltipModule} from '@snelstart/ui-tooltip';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-resultaten-rekening',
  templateUrl: './resultaten-rekening.component.html',
  styleUrls: ['./resultaten-rekening.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    CommonModule,
    SharedUiTooltipModule
  ]
})
export class ResultatenRekeningComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef = inject(DestroyRef);

  public balansEnResultatenRekeningFg: FormGroup = this.dataService.createBalansEnResultatenRekeningFormGroup();
  public isFE = false;
  public berekendeResultatenFg = new FormGroup({
    saldoFiscaleWinstberekening: new FormControl<number | null>(0)
  });

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      tap(data => {
        if (!data.aangifte) {
          return;
        }

        this.isFE = data.aangifte.identificatie.aangifteFiscaleEenheid;
        this.balansEnResultatenRekeningFg.patchValue(data.aangifte.balansEnResultatenrekening, {emitEvent: false});
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly vpbBerekening$: Observable<VpbBerekening> =
    this.dataService.vpbBerekening$.pipe(
      filter((data): data is VpbBerekening => !!data),
      tap(vpbBerekening => {
        const resultaat = vpbBerekening.jaarstukken?.resultaat;
        if (resultaat) {
          this.berekendeResultatenFg.patchValue(resultaat, {emitEvent: false});
        }
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.balansEnResultatenRekeningFg.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.saveForm();
      this.dataService.calculateLatestCorporateTaxReturn().subscribe();
    });
  }

  ngOnDestroy(): void {
    this.saveForm();
    this.dataService.calculateLatestCorporateTaxReturn().subscribe();
  }

  getArrayControls(arrayName: string): AbstractControl[] {
    return (this.balansEnResultatenRekeningFg.get(arrayName) as FormArray).controls;
  }

  saveForm(): void {
    this.balansEnResultatenRekeningFg.updateValueAndValidity({emitEvent: false});
    const balansEnResultatenrekening = this.balansEnResultatenRekeningFg.getRawValue();

    this.dataService.patchCorporateTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          balansEnResultatenrekening
        } as VpbAangifte
      } as CorporateTaxEntity;
    });
  }
}
